import { Box, Link, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import VastEdgeLogo from "../images/vastedge-logo-white-small.png";

const FEATURES = [
  { Icon: AutoAwesomeIcon, title: "AI-assisted negotiation", text: "Smart settlement ranges and offer insights." },
  { Icon: LockOutlinedIcon, title: "Secure case documents", text: "Share evidence only with the right parties." },
  { Icon: VideoCameraFrontOutlinedIcon, title: "Virtual hearings", text: "Meet over Zoom or Teams from one place." },
];

// Shared primary button style for all auth pages.
export const authButtonClass =
  "!py-[10.4px] !text-[15px] !text-white !bg-[linear-gradient(120deg,#1d3a8a_0%,#2f6fd6_55%,#1fb5c9_100%)] " +
  "!shadow-[0_10px_24px_-8px_rgba(47,111,214,0.7)] !transition-[transform,box-shadow] !duration-150 !ease-in-out " +
  "hover:!-translate-y-px hover:!shadow-[0_14px_28px_-8px_rgba(47,111,214,0.85)] " +
  "hover:!bg-[linear-gradient(120deg,#1d3a8a_0%,#2f6fd6_55%,#1fb5c9_100%)] " +
  "[&.Mui-disabled]:!text-white [&.Mui-disabled]:!opacity-75";

// Literal class names so Tailwind can see them.
const FEATURE_DELAYS = ["[animation-delay:150ms]", "[animation-delay:270ms]", "[animation-delay:390ms]"];

// Position, size, colour and delay come in as Tailwind classes.
function Orb({ className }) {
  return (
    <Box
      className={`absolute rounded-full blur-[70px] opacity-[0.55] pointer-events-none animate-float ${className}`}
    />
  );
}

export default function AuthLayout({ title, subtitle, icon, children, cardWidth = 440, showFeatures = true }) {
  return (
    <Box className="min-h-screen relative overflow-hidden bg-[linear-gradient(135deg,#061a3a_0%,#0b3474_45%,#0c6f9c_100%)] flex flex-col">
      <Orb className="w-[420px] h-[420px] bg-[#2f6fd6] top-[-120px] left-[-100px] [animation-delay:0s]" />
      <Orb className="w-[360px] h-[360px] bg-[#1fb5c9] bottom-[-120px] right-[-60px] [animation-delay:-5s]" />
      <Orb className="w-[260px] h-[260px] bg-[#7b5cff] top-[40%] left-[35%] [animation-delay:-9s]" />
      <Box className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)] [-webkit-mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)] pointer-events-none" />

      <Box className="relative px-4 min-[900px]:px-10 pt-5">
        <Link href="https://www.vastedge.com/">
          <img src={VastEdgeLogo} height="42" alt="VastEdge" />
        </Link>
      </Box>

      <Box className="relative flex-1 flex items-center justify-center min-[1200px]:gap-24 px-4 min-[900px]:px-10 py-8">
        <Box
          className={`${showFeatures ? "hidden min-[1200px]:block" : "hidden"} max-w-[440px] text-white animate-rise-slow`}
        >
          <Box className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-[20px] border border-solid border-[rgba(255,255,255,0.25)] bg-[rgba(255,255,255,0.08)] text-[13px]">
            <GavelOutlinedIcon className="!text-[16px]" /> Online Dispute Resolution
          </Box>
          <Typography className="min-[900px]:text-[38px] min-[1200px]:text-[44px] font-bold leading-[1.15]">
            Resolve disputes{" "}
            <Box
              component="span"
              className="bg-[linear-gradient(90deg,#7fd3ff,#5ef0d6)] bg-clip-text [-webkit-text-fill-color:transparent]"
            >
              faster & fairer
            </Box>
          </Typography>
          <Typography className="mt-4 opacity-75 text-[16px] leading-[1.6]">
            One secure workspace for cases, documents, hearings and settlement.
          </Typography>

          <Box mt={4} display="flex" flexDirection="column" gap={2}>
            {FEATURES.map(({ Icon, title: featureTitle, text }, i) => (
              <Box
                key={featureTitle}
                display="flex"
                gap={2}
                alignItems="center"
                className={`animate-rise-slow ${FEATURE_DELAYS[i]}`}
              >
                <Box className="w-[44px] h-[44px] shrink-0 rounded-xl grid place-items-center bg-[rgba(255,255,255,0.1)] border border-solid border-[rgba(255,255,255,0.18)] backdrop-blur-[6px]">
                  <Icon className="!text-[22px] text-[#7fd3ff]" />
                </Box>
                <Box>
                  <Typography fontWeight={600} fontSize={15}>
                    {featureTitle}
                  </Typography>
                  <Typography fontSize={13} className="opacity-70">
                    {text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          className="animate-rise w-full p-6 min-[600px]:p-8 rounded-[20px] bg-[rgba(255,255,255,0.94)] backdrop-blur-[18px] border border-solid border-[rgba(255,255,255,0.6)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.8)] [&&_.MuiOutlinedInput-root]:rounded-[12px] [&&_.MuiOutlinedInput-root]:bg-[#f5f8fc] [&&_.MuiOutlinedInput-root]:[transition:box-shadow_0.2s_ease,background-color_0.2s_ease] [&&_.MuiOutlinedInput-root:hover]:bg-[#eef3fa] [&&_.MuiOutlinedInput-root.Mui-focused]:bg-white [&&_.MuiOutlinedInput-root.Mui-focused]:shadow-[0_0_0_4px_rgba(47,111,214,0.12)]"
          // maxWidth is a runtime prop, so it stays an inline style
          style={{ maxWidth: cardWidth }}
        >
          {icon ? (
            <Box className="w-[52px] h-[52px] mb-4 rounded-xl grid place-items-center text-white bg-[linear-gradient(135deg,#2f6fd6,#1fb5c9)] shadow-[0_10px_22px_-8px_rgba(47,111,214,0.8)]">
              {icon}
            </Box>
          ) : null}
          <Typography variant="h5" fontWeight={700} color="#0d1b36">
            {title}
          </Typography>
          {subtitle ? (
            <Typography fontSize={14} color="text.secondary" mt={0.5}>
              {subtitle}
            </Typography>
          ) : null}
          <Box mt={3}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
